// usePasskey Hook
// React hook for managing passkey operations

import { useState, useEffect, useCallback } from 'react';
import { WebAuthnService } from '../lib/webauthn-service';
import { WebAuthnUtils } from '../lib/webauthn-utils';
import type {
  UsePasskeyReturn,
  WebAuthnSupport,
  WebAuthnError,
  PasskeyListItem,
  PasskeyRegistrationRequest,
  PasskeyAuthenticationRequest
} from '../types/webauthn';

export function usePasskey(userId?: string): UsePasskeyReturn {
  // State
  const [isSupported, setIsSupported] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [credentials, setCredentials] = useState<PasskeyListItem[]>([]);
  const [error, setError] = useState<WebAuthnError | null>(null);
  const [support, setSupport] = useState<WebAuthnSupport | null>(null);

  // Check WebAuthn support on mount
  useEffect(() => {
    checkSupport();
  }, []);

  // Load user credentials when userId changes
  useEffect(() => {
    if (userId && isSupported) {
      refreshCredentials();
    } else {
      setCredentials([]);
    }
  }, [userId, isSupported]);

  /**
   * Check WebAuthn support
   */
  const checkSupport = useCallback(async (): Promise<WebAuthnSupport> => {
    try {
      const supportInfo = await WebAuthnUtils.WebAuthnDetector.checkSupport();
      setSupport(supportInfo);
      setIsSupported(supportInfo.available);
      return supportInfo;
    } catch (err) {
      const webauthnError = WebAuthnUtils.WebAuthnErrorHandler.handleError(err);
      setError(webauthnError);
      setIsSupported(false);
      return {
        available: false,
        platform_authenticator: false,
        cross_platform_authenticator: false,
        user_verification: false,
        resident_key: false,
        conditional_mediation: false,
        large_blob: false
      };
    }
  }, []);

  /**
   * Refresh user credentials
   */
  const refreshCredentials = useCallback(async (): Promise<void> => {
    if (!userId) return;

    try {
      setIsLoading(true);
      setError(null);
      const userCredentials = await WebAuthnService.getUserCredentials(userId);
      setCredentials(userCredentials);
    } catch (err) {
      const webauthnError = WebAuthnUtils.WebAuthnErrorHandler.handleError(err);
      setError(webauthnError);
      console.error('Error refreshing credentials:', webauthnError);
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  /**
   * Register a new passkey
   */
  const register = useCallback(async (options: PasskeyRegistrationRequest): Promise<boolean> => {
    if (!isSupported) {
      setError({
        name: 'NotSupportedError',
        message: 'WebAuthn não é suportado neste navegador'
      });
      return false;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Start registration
      const creationOptions = await WebAuthnService.startRegistration(options);

      // Create credential
      const credential = await navigator.credentials.create({
        publicKey: creationOptions
      }) as PublicKeyCredential;

      if (!credential) {
        throw new Error('Failed to create credential');
      }

      // Complete registration
      await WebAuthnService.completeRegistration(
        options.user_id,
        credential,
        options.credential_name
      );

      // Refresh credentials list
      await refreshCredentials();

      return true;
    } catch (err) {
      const webauthnError = WebAuthnUtils.WebAuthnErrorHandler.handleError(err);
      setError(webauthnError);
      console.error('Registration error:', webauthnError);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isSupported, refreshCredentials]);

  /**
   * Authenticate with passkey
   */
  const authenticate = useCallback(async (options?: PasskeyAuthenticationRequest): Promise<boolean> => {
    if (!isSupported) {
      setError({
        name: 'NotSupportedError',
        message: 'WebAuthn não é suportado neste navegador'
      });
      return false;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Start authentication
      const requestOptions = await WebAuthnService.startAuthentication(options);

      // Get credential
      const credential = await navigator.credentials.get({
        publicKey: requestOptions
      }) as PublicKeyCredential;

      if (!credential) {
        throw new Error('Failed to get credential');
      }

      // Complete authentication
      const result = await WebAuthnService.completeAuthentication(credential);

      // Refresh credentials list if we have a user
      if (userId) {
        await refreshCredentials();
      }

      return true;
    } catch (err) {
      const webauthnError = WebAuthnUtils.WebAuthnErrorHandler.handleError(err);
      setError(webauthnError);
      console.error('Authentication error:', webauthnError);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isSupported, userId, refreshCredentials]);

  /**
   * Delete a credential
   */
  const deleteCredential = useCallback(async (credentialId: string): Promise<boolean> => {
    if (!userId) {
      setError({
        name: 'InvalidStateError',
        message: 'User ID is required to delete credentials'
      });
      return false;
    }

    try {
      setIsLoading(true);
      setError(null);

      await WebAuthnService.deleteCredential(credentialId, userId);
      await refreshCredentials();

      return true;
    } catch (err) {
      const webauthnError = WebAuthnUtils.WebAuthnErrorHandler.handleError(err);
      setError(webauthnError);
      console.error('Delete credential error:', webauthnError);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [userId, refreshCredentials]);

  /**
   * Update credential name
   */
  const updateCredential = useCallback(async (credentialId: string, name: string): Promise<boolean> => {
    if (!userId) {
      setError({
        name: 'InvalidStateError',
        message: 'User ID is required to update credentials'
      });
      return false;
    }

    try {
      setIsLoading(true);
      setError(null);

      await WebAuthnService.updateCredentialName(credentialId, userId, name);
      await refreshCredentials();

      return true;
    } catch (err) {
      const webauthnError = WebAuthnUtils.WebAuthnErrorHandler.handleError(err);
      setError(webauthnError);
      console.error('Update credential error:', webauthnError);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [userId, refreshCredentials]);

  /**
   * Clear current error
   */
  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  /**
   * Check if user can register new passkeys
   */
  const canRegister = useCallback((): boolean => {
    if (!isSupported || !support) return false;
    
    // Check if we have platform or cross-platform authenticators
    return support.platform_authenticator || support.cross_platform_authenticator;
  }, [isSupported, support]);

  /**
   * Check if conditional mediation is available (autofill)
   */
  const hasConditionalMediation = useCallback((): boolean => {
    return support?.conditional_mediation || false;
  }, [support]);

  /**
   * Get authentication options for autofill
   */
  const getAutofillOptions = useCallback(async (): Promise<PublicKeyCredentialRequestOptions | null> => {
    if (!hasConditionalMediation() || !userId) return null;

    try {
      const options = await WebAuthnService.startAuthentication({ user_id: userId });
      return options;
    } catch (err) {
      console.error('Error getting autofill options:', err);
      return null;
    }
  }, [hasConditionalMediation, userId]);

  /**
   * Start conditional mediation (autofill) authentication
   */
  const startConditionalAuthentication = useCallback(async (): Promise<boolean> => {
    if (!hasConditionalMediation()) return false;

    try {
      const options = await getAutofillOptions();
      if (!options) return false;

      // Start conditional mediation
      const credential = await navigator.credentials.get({
        publicKey: options,
        mediation: 'conditional'
      }) as PublicKeyCredential;

      if (!credential) return false;

      // Complete authentication
      await WebAuthnService.completeAuthentication(credential);
      return true;
    } catch (err) {
      console.error('Conditional authentication error:', err);
      return false;
    }
  }, [hasConditionalMediation, getAutofillOptions]);

  return {
    // State
    isSupported,
    isLoading,
    credentials,
    error,
    support,
    
    // Actions
    register,
    authenticate,
    deleteCredential,
    updateCredential,
    refreshCredentials,
    
    // Utilities
    checkSupport,
    clearError,
    
    // Advanced features
    canRegister,
    hasConditionalMediation,
    getAutofillOptions,
    startConditionalAuthentication
  };
}

export default usePasskey;